import { useApolloClient } from "@apollo/client/react";
import { get } from "lodash";
import { useCallback, useMemo } from "react";

import {
  createDesign as createDesignMutation,
  deleteDesign as deleteDesignMutation,
  getDesign as getDesignQuery,
  getDesignForWorkspace as getDesignForWorkspaceQuery,
  designTypes as designTypesQuery,
  designWorkflowStages as designWorkflowStagesQuery,
  listDesigns as listDesignsQuery,
  updateDesign as updateDesignMutation,
} from "./designs.graphql";
import {
  createDesignOption as createDesignOptionMutation,
  deleteDesignOption as deleteDesignOptionMutation,
  updateDesignOption as updateDesignOptionMutation,
} from "./overlay/designOption.graphql";
import {
  buildDesignMutationInput,
  type DesignMutationFormInput,
} from "./utils";
import type { Design, DesignMetaOption } from "./designUtils";

/** A design option row as returned by the overlay option mutations. */
export type DesignOptionRecord = {
  id: string;
  designId: string;
  name: string;
  isTrunk: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export const useDesignsRepository = () => {
  const client = useApolloClient();

  const listDesigns = useCallback(async (): Promise<Design[]> => {
    const { data } = await client.query({
      query: listDesignsQuery,
      fetchPolicy: "network-only",
    });
    return get(data, "designs", []);
  }, [client]);

  const getDesign = useCallback(
    async ({ id }: { id: string }): Promise<Design | null> => {
      const { data } = await client.query({
        query: getDesignQuery,
        variables: { id },
        fetchPolicy: "network-only",
      });
      return get(data, "design", null);
    },
    [client]
  );

  const getDesignForWorkspace =
    useCallback(async (): Promise<Design | null> => {
      const { data } = await client.query({
        query: getDesignForWorkspaceQuery,
        fetchPolicy: "network-only",
      });
      return get(data, "design", null);
    }, [client]);

  const createDesign = useCallback(
    async ({
      input,
    }: {
      input: DesignMutationFormInput;
    }): Promise<Design | null> => {
      const { data } = await client.mutate({
        mutation: createDesignMutation,
        variables: { input: buildDesignMutationInput(input) },
      });
      return get(data, "design", null);
    },
    [client]
  );

  const updateDesign = useCallback(
    async ({
      id,
      input,
    }: {
      id: string;
      input: DesignMutationFormInput;
    }): Promise<Design | null> => {
      const { data } = await client.mutate({
        mutation: updateDesignMutation,
        variables: { id, input: buildDesignMutationInput(input) },
      });
      return get(data, "design", null);
    },
    [client]
  );

  const deleteDesign = useCallback(
    async ({ id }: { id: string }): Promise<boolean> => {
      const { data } = await client.mutate({
        mutation: deleteDesignMutation,
        variables: { id },
      });
      return get(data, "deleteDesign.deleted", false);
    },
    [client]
  );

  const getWorkflowStages = useCallback(async (): Promise<
    DesignMetaOption[]
  > => {
    const { data } = await client.query({
      query: designWorkflowStagesQuery,
    });
    return get(data, "designWorkflowStages", []);
  }, [client]);

  const getDesignTypes = useCallback(async (): Promise<DesignMetaOption[]> => {
    const { data } = await client.query({
      query: designTypesQuery,
    });
    return get(data, "designTypes", []);
  }, [client]);

  const createDesignOption = useCallback(
    async ({
      id,
      name,
    }: {
      id: string;
      name: string;
    }): Promise<DesignOptionRecord | null> => {
      const { data } = await client.mutate({
        mutation: createDesignOptionMutation,
        variables: { id, input: { name } },
      });
      return get(data, "option", null);
    },
    [client]
  );

  const renameDesignOption = useCallback(
    async ({
      id,
      optionId,
      name,
    }: {
      id: string;
      optionId: string;
      name: string;
    }): Promise<DesignOptionRecord | null> => {
      const { data } = await client.mutate({
        mutation: updateDesignOptionMutation,
        variables: { id, optionId, input: { name } },
      });
      return get(data, "option", null);
    },
    [client]
  );

  const deleteDesignOption = useCallback(
    async ({
      id,
      optionId,
    }: {
      id: string;
      optionId: string;
    }): Promise<boolean> => {
      const { data } = await client.mutate({
        mutation: deleteDesignOptionMutation,
        variables: { id, optionId },
      });
      return get(data, "deleteDesignOption.deleted", false);
    },
    [client]
  );

  return useMemo(
    () => ({
      listDesigns,
      getDesign,
      getDesignForWorkspace,
      createDesign,
      updateDesign,
      deleteDesign,
      getWorkflowStages,
      getDesignTypes,
      createDesignOption,
      renameDesignOption,
      deleteDesignOption,
    }),
    [
      listDesigns,
      getDesign,
      getDesignForWorkspace,
      createDesign,
      updateDesign,
      deleteDesign,
      getWorkflowStages,
      getDesignTypes,
      createDesignOption,
      renameDesignOption,
      deleteDesignOption,
    ]
  );
};

export default useDesignsRepository;
